"use client";

import React, { useEffect, useState } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import Script from "next/script";
import { useSelectedTherapist, useQuiz } from "../../../contexts/AppContext";
import { useUser } from "../../../contexts/UserContext";
import { auditService } from "@repo/supabase/services";
import {
  TherapistProfileSidebar,
  TherapistWelcomeBanner,
  TherapistAboutSection,
  AreasOfFocusSection,
  ModalitiesSection,
  TherapistPracticeInfo,
  TherapistCertifications
} from "@repo/ui";
import { CREDENTIAL_FULL_NAMES } from "@repo/supabase";
import { LoginForm } from "../../../components/LoginForm";
import { WaitlistModal } from "./WaitlistModal";

interface TherapistProfileClientProps {
  therapist: any; // You can type this properly based on your data structure
}

export default function TherapistProfileClient({ therapist }: TherapistProfileClientProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { profile: userProfile, loadProfile } = useUser();
  const { setSelectedTherapist } = useSelectedTherapist();
  const { quizAnswers, setQuizAnswers } = useQuiz();
  const fromQuiz = searchParams.get('from') === 'quiz';
  const matchingQuizId = searchParams.get('matchingQuizId');
  const emailFromUrl = searchParams.get('email');
  const partnershipId = searchParams.get('partnershipId');

  // Check for special booking parameter to override accepting status
  const allowBooking = searchParams.get('allowBooking') === 'true';

  // Login modal state
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);

  // Get therapist slug from URL path (safer than relying on therapist.slug)
  const pathSegments = typeof window !== 'undefined' ? window.location.pathname.split('/') : [];
  const therapistSlug = pathSegments[2] || therapist.slug; // fallback to therapist.slug if available

  // Use quiz data from context
  const effectiveQuizData = quizAnswers;

  // Check if user has essential quiz data OR has matchingQuizId with email in URL
  // If we have matchingQuizId and email, we can fetch the quiz data later
  const hasEssentialQuizData = !!(
    (effectiveQuizData?.rate &&
     effectiveQuizData?.timezone &&
     effectiveQuizData?.sessionType &&
     effectiveQuizData?.therapistType) ||
    (matchingQuizId && emailFromUrl)
  );

  // Is this therapist actually accepting NEW clients for the visitor's session type?
  //  - If we know the visitor's sessionType (from the quiz), gate on that specific flag.
  //  - If we don't, fall back to "accepts any" (matches previous behavior for anonymous visitors
  //    hitting the profile directly — they'll be forced through the quiz which then re-checks).
  //  - `allowBooking` (from ?allowBooking=true on the URL) is the admin-override escape hatch.
  const desiredSessionType = effectiveQuizData?.sessionType;
  const isAcceptingForVisitor =
    allowBooking
      ? true
      : desiredSessionType === 'individual'
        ? therapist.isAcceptingIndividual === true
        : desiredSessionType === 'couples'
          ? therapist.isAcceptingCouples === true
          : therapist.availability.status === 'Accepting new clients';
  
  // Log therapist profile view for audit
  useEffect(() => {
    const logProfileView = async () => {
      if (therapist) {
        // Only log if we have a valid user ID (from UserContext or quiz data)
        const logUserId = userProfile?.id || effectiveQuizData?.userId;

        if (logUserId) {
          console.log('[TherapistProfile] Logging profile view for userId:', logUserId);

          await auditService.log({
            userId: logUserId as string,
            action: 'data.access',
            resource: 'therapist_profile',
            resourceId: therapist.id,
            details: {
              userRole: 'client',
              therapistName: `${therapist.firstName} ${therapist.lastName}`,
              therapistSlug: therapistSlug,
              accessContext: fromQuiz ? 'from_quiz' : 'direct_access',
              hasQuizData: hasEssentialQuizData,
              quizId: effectiveQuizData?.matchingQuizId || matchingQuizId || null,
              viewedBy: userProfile?.id || 'anonymous',
              timestamp: new Date().toISOString()
            }
          }).catch(err => {
            console.error('[TherapistProfile] Failed to log profile view:', err);
          });
        } else {
          // For truly anonymous users without quiz data, we can't log to audit_logs
          // because userId must be a valid UUID from the users table
          console.log('[TherapistProfile] Anonymous view - no userId available for logging');
        }
      }
    };

    logProfileView();
  }, [userProfile, therapist.id]); // Only log once per therapist view
  
  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": therapist.displayName,
    "givenName": therapist.firstName,
    "familyName": therapist.lastName,
    "image": therapist.profileImage,
    "jobTitle": therapist.credential || "Intern Therapist",
    "worksFor": {
      "@type": "Organization",
      "name": "Alli Therapy"
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "CA"
    },
    "description": therapist.bio,
    "knowsAbout": therapist.uniqueUnderstanding || [],
    "offers": {
      "@type": "Offer",
      "priceRange": therapist.rates.range,
      "priceCurrency": "CAD",
      "itemOffered": {
        "@type": "Service",
        "name": "Therapy Session",
        "duration": therapist.rates.duration
      }
    }
  };
  
  return (
    <>
      <Script
        id="therapist-structured-data"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <div className="min-h-screen bg-white">
      {/* Back to matches button when coming from quiz */}
      {fromQuiz && (
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <button
              onClick={() => {
                // Navigate to matched page with the matchingQuizId
                if (matchingQuizId) {
                  router.push(`/matched?id=${matchingQuizId}`);
                } else {
                  router.back();
                }
              }}
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to your matches
            </button>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <div className="relative max-w-7xl mx-auto px-4 py-8 md:py-12">
        <div className="grid md:grid-cols-12 gap-8 items-start">
          {/* Left Column - Profile Info */}
          <div className="md:col-span-4 md:h-fit">
            <TherapistProfileSidebar
              firstName={therapist.firstName}
              lastName={therapist.lastName}
              displayName={therapist.displayName}
              credential={therapist.credential}
              profileImage={therapist.profileImage}
              acceptingClients={isAcceptingForVisitor}
              therapistTier={therapist.therapistTier}
              pronoun={therapist.pronoun}
              onCtaClick={() => {
                if (isAcceptingForVisitor) {
                  // Save therapist to context when user clicks book
                  console.log('[TherapistProfile] Saving therapist to context before booking:', therapist.displayName);
                  setSelectedTherapist(therapist);
                  
                  // Check if user has essential quiz data
                  if (!hasEssentialQuizData) {
                    // Pre-set the therapist type based on the therapist they're booking
                    const updatedAnswers = {
                      ...effectiveQuizData,
                      therapistType: therapist.therapistTier // 'intern' or 'associate'
                    };
                    setQuizAnswers(updatedAnswers);
                    
                    // Redirect to quick quiz with therapist info
                    const quizParams = new URLSearchParams();
                    quizParams.append('therapist', therapistSlug);
                    if (partnershipId) quizParams.append('partnershipId', partnershipId);
                    if (allowBooking) quizParams.append('allowBooking', 'true');
                    router.push(`/quick-quiz?${quizParams.toString()}`);
                  } else if (userProfile) {
                    // User is authenticated - proceed directly to booking
                    const params = new URLSearchParams();
                    if (matchingQuizId) params.append('matchingQuizId', matchingQuizId);
                    if (emailFromUrl) params.append('email', emailFromUrl);
                    if (partnershipId) params.append('partnershipId', partnershipId);
                    if (allowBooking) params.append('allowBooking', 'true');
                    // Include quiz data
                    if (effectiveQuizData?.sessionType) params.append('sessionType', effectiveQuizData.sessionType);
                    if (effectiveQuizData?.therapistType) params.append('therapistType', effectiveQuizData.therapistType);
                    if (effectiveQuizData?.rate) params.append('rate', effectiveQuizData.rate.toString());
                    if (effectiveQuizData?.timezone) params.append('timezone', effectiveQuizData.timezone);
                    if (effectiveQuizData?.promoCode) params.append('promoCode', effectiveQuizData.promoCode);
                    const bookingUrl = `/therapist/${therapistSlug}/book${params.toString() ? `?${params.toString()}` : ''}`;
                    router.push(bookingUrl);
                  } else if (emailFromUrl) {
                    // User not authenticated but has email from URL - show login modal
                    console.log('[TherapistProfile] Showing login modal for email:', emailFromUrl);
                    setShowLoginModal(true);
                  } else {
                    // User has quiz data but no user info at all - need to authenticate
                    const params = new URLSearchParams();
                    if (matchingQuizId) params.append('matchingQuizId', matchingQuizId);
                    if (partnershipId) params.append('partnershipId', partnershipId);
                    if (allowBooking) params.append('allowBooking', 'true');
                    const bookingUrl = `/therapist/${therapistSlug}/book${params.toString() ? `?${params.toString()}` : ''}`;
                    const returnTo = encodeURIComponent(bookingUrl);
                    router.push(`/login?returnTo=${returnTo}`);
                  }
                } else {
                  setShowWaitlistModal(true);
                }
              }}
            />
          </div>
          
          {/* Right Column - Main Content */}
          <div className="md:col-span-8">
            {/* Welcome Banner */}
            <div className="mb-8">
              <TherapistWelcomeBanner
                firstName={therapist.firstName}
                acceptingClients={allowBooking || therapist.availability.status === "Accepting new clients"}
                acceptingFor={therapist.availability.acceptingFor}
                nextAvailable={therapist.availability.nextAvailable}
                bookingNote={therapist.availability.bookingNote}
                rateRange={therapist.rates.range}
                sessionDuration={therapist.rates.duration}
                clientTypes={therapist.clientTypes}
                therapistTier={therapist.therapistTier}
                credentialsWithFullNames={therapist.credentials.map((type: string) => ({
                  type,
                  fullName: CREDENTIAL_FULL_NAMES[type] || type
                }))}
                employmentStatus={therapist.employmentStatus}
                credentials={therapist.credentials}
                isApprovedForCouples={therapist.isApprovedForCouples}
                isAcceptingCouples={therapist.isAcceptingCouples}
              />
            </div>
            
            {/* All Content in One Scroll */}
            <div className="space-y-12">
              {/* About Section */}
              <TherapistAboutSection
                firstName={therapist.firstName}
                bio={therapist.bio}
                uniqueUnderstanding={therapist.uniqueUnderstanding}
                clientDescriptions={therapist.clientDescriptions}
                rightFitFor={therapist.rightFitFor}
                whatToExpect={therapist.whatToExpect}
              />
              
              {/* Expertise Section */}
              <section id="expertise" className="scroll-mt-8 space-y-8">
                {/* Areas of Focus */}
                <AreasOfFocusSection areasOfFocus={therapist.areasOfFocus} />
                
                {/* Therapeutic Frameworks & Approach */}
                <ModalitiesSection modalities={therapist.modalities} therapistTier={therapist.therapistTier} />
              </section>
              
              {/* Practice Information Section */}
              <section id="logistics" className="scroll-mt-8 space-y-8">
                <TherapistPracticeInfo
                  schedule={therapist.availability.schedule}
                  timezone={therapist.availability.timezone}
                  therapistTier={therapist.therapistTier}
                  credential={therapist.credential}
                  credentialsWithFullNames={
                    therapist.therapistTier === 'resident' && therapist.supervisor?.credentials
                      ? therapist.supervisor.credentials.map((type: string) => ({
                          type,
                          fullName: CREDENTIAL_FULL_NAMES[type] || type
                        }))
                      : therapist.credentials.map((type: string) => ({
                          type,
                          fullName: CREDENTIAL_FULL_NAMES[type] || type
                        }))
                  }
                  supervisor={therapist.supervisor}
                  residencyCompletion={therapist.residencyCompletion}
                  firstName={therapist.firstName}
                  languages={therapist.languages}
                  onExploreAvailability={() => {
                    // Handle explore availability
                  }}
                />
                
                {/* Certifications Section */}
                <TherapistCertifications
                  certifications={therapist.certifications}
                  firstName={therapist.firstName}
                />
              </section>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Sticky CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t p-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">{therapist.firstName}</p>
            <p className="text-sm text-gray-600">
              From <span className="text-gray-900">
                ${therapist.therapistTier === 'resident' ? '45' : '125'}
              </span>/session
            </p>
          </div>
          {isAcceptingForVisitor ? (
            <button
              onClick={() => {
                // Save therapist to context when user clicks book
                console.log('[TherapistProfile Mobile] Saving therapist to context before booking:', therapist.displayName);
                setSelectedTherapist(therapist);
                
                // Check if user has essential quiz data
                if (!hasEssentialQuizData) {
                  // Pre-set the therapist type based on the therapist they're booking
                  const updatedAnswers = {
                    ...effectiveQuizData,
                    therapistType: therapist.therapistTier // 'intern' or 'associate'
                  };
                  setQuizAnswers(updatedAnswers);
                  
                  // Redirect to quick quiz with therapist info
                  const quizParams = new URLSearchParams();
                  quizParams.append('therapist', therapistSlug);
                  if (partnershipId) quizParams.append('partnershipId', partnershipId);
                  if (allowBooking) quizParams.append('allowBooking', 'true');
                  router.push(`/quick-quiz?${quizParams.toString()}`);
                } else if (userProfile) {
                  // User is authenticated - proceed directly to booking
                  const params = new URLSearchParams();
                  if (matchingQuizId) params.append('matchingQuizId', matchingQuizId);
                  if (emailFromUrl) params.append('email', emailFromUrl);
                  if (partnershipId) params.append('partnershipId', partnershipId);
                  if (allowBooking) params.append('allowBooking', 'true');
                  // Include quiz data
                  if (effectiveQuizData?.sessionType) params.append('sessionType', effectiveQuizData.sessionType);
                  if (effectiveQuizData?.therapistType) params.append('therapistType', effectiveQuizData.therapistType);
                  if (effectiveQuizData?.rate) params.append('rate', effectiveQuizData.rate.toString());
                  if (effectiveQuizData?.timezone) params.append('timezone', effectiveQuizData.timezone);
                  if (effectiveQuizData?.promoCode) params.append('promoCode', effectiveQuizData.promoCode);
                  const bookingUrl = `/therapist/${therapistSlug}/book${params.toString() ? `?${params.toString()}` : ''}`;
                  router.push(bookingUrl);
                } else if (emailFromUrl) {
                  // User not authenticated but has email from URL - show login modal
                  console.log('[TherapistProfile Mobile] Showing login modal for email:', emailFromUrl);
                  setShowLoginModal(true);
                } else {
                  // User has quiz data but no user info at all - need to authenticate
                  const params = new URLSearchParams();
                  if (matchingQuizId) params.append('matchingQuizId', matchingQuizId);
                  if (partnershipId) params.append('partnershipId', partnershipId);
                  if (allowBooking) params.append('allowBooking', 'true');
                  const bookingUrl = `/therapist/${therapistSlug}/book${params.toString() ? `?${params.toString()}` : ''}`;
                  const returnTo = encodeURIComponent(bookingUrl);
                  router.push(`/login?returnTo=${returnTo}`);
                }
              }}
              className="bg-primary-500 hover:bg-primary-600 text-white font-semibold py-2.5 px-5 rounded-full shadow-sm transition-colors"
            >
              Book a Session
            </button>
          ) : (
            <button
              onClick={() => setShowWaitlistModal(true)}
              className="bg-white text-primary-500 border border-cyan-700 hover:border-cyan-700 hover:bg-primary-100 font-semibold py-2.5 px-5 rounded-full shadow-none transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Join Waitlist
            </button>
          )}
        </div>
      </div>

      {/* Waitlist Modal */}
      <WaitlistModal
        isOpen={showWaitlistModal}
        onClose={() => setShowWaitlistModal(false)}
        therapistName={therapist.firstName}
        therapistEmail={therapist.email || `${therapist.firstName.toLowerCase()}@alli.io`}
      />

      {/* Login Modal */}
      {showLoginModal && emailFromUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 relative">
            {/* Close Button */}
            <button
              onClick={() => setShowLoginModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Header explaining why this modal appeared */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 rounded-full mb-3">
                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">
                Sign in to book your session
              </h2>
              <p className="text-sm text-gray-600">
                We&apos;ll match this booking to your account. New here?{" "}
                <span className="text-primary-600 font-medium">Use the &quot;Sign up&quot; link below.</span>
              </p>
            </div>

            {/* Login Form */}
            <LoginForm
              defaultToMagicLink={true}
              userEmail={emailFromUrl}
              emailReadOnly={true}
              redirectTo={(() => {
                // After login or signup-from-here, take the visitor straight
                // to /book with the quiz context so they pick a slot next.
                const params = new URLSearchParams();
                if (matchingQuizId) params.append('matchingQuizId', matchingQuizId);
                if (emailFromUrl) params.append('email', emailFromUrl);
                if (partnershipId) params.append('partnershipId', partnershipId);
                if (allowBooking) params.append('allowBooking', 'true');
                if (effectiveQuizData?.sessionType) params.append('sessionType', String(effectiveQuizData.sessionType));
                if (effectiveQuizData?.therapistType) params.append('therapistType', String(effectiveQuizData.therapistType));
                if (effectiveQuizData?.rate) params.append('rate', String(effectiveQuizData.rate));
                if (effectiveQuizData?.timezone) params.append('timezone', String(effectiveQuizData.timezone));
                if (effectiveQuizData?.promoCode) params.append('promoCode', String(effectiveQuizData.promoCode));
                return `/therapist/${therapistSlug}/book${params.toString() ? `?${params.toString()}` : ''}`;
              })()}
              onSuccess={async () => {
                // Reload profile after successful login
                console.log('[TherapistProfile] Login successful, refreshing page');
                setShowLoginModal(false);

                // Wait a bit for the session to be established
                setTimeout(() => {
                  // Refresh the page to pick up auth state
                  // Using href assignment instead of reload() to preserve URL and reprocess through middleware
                  window.location.href = window.location.href;
                }, 500);
              }}
            />
          </div>
        </div>
      )}
    </div>
    </>
  );
}